import { Suspense, useEffect, useRef, useState, type ReactNode } from "react";

interface DeferredHomeSectionProps {
  children: ReactNode;
  placeholderClassName?: string;
}

function SectionPlaceholder({ className = "min-h-[16rem]" }: { className?: string }) {
  return (
    <div className={`container py-10 ${className}`} aria-hidden="true">
      <div className="mx-auto h-32 max-w-5xl animate-pulse rounded-2xl bg-muted/35" />
    </div>
  );
}

export function DeferredHomeSection({
  children,
  placeholderClassName,
}: DeferredHomeSectionProps) {
  const [ready, setReady] = useState(false);
  const markerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const marker = markerRef.current;
    if (ready || !marker) return;

    if (typeof window.IntersectionObserver !== "function") {
      const fallbackTimer = window.setTimeout(() => setReady(true), 0);
      return () => window.clearTimeout(fallbackTimer);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        setReady(true);
      },
      { rootMargin: "1000px 0px" },
    );
    observer.observe(marker);
    return () => observer.disconnect();
  }, [ready]);

  const placeholder = <SectionPlaceholder className={placeholderClassName} />;

  return (
    <div
      ref={markerRef}
      data-home-deferred-section
      data-ready={ready ? "true" : "false"}
    >
      {ready ? <Suspense fallback={placeholder}>{children}</Suspense> : placeholder}
    </div>
  );
}
